import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  getParticipantFunnel,
  listBetaNotes,
  addBetaNote,
  deleteBetaNote,
  upsertBetaParticipant,
} from "@/lib/beta.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { ArrowLeft, Check, Clock, AlertCircle, Minus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/beta/$userId")({
  head: () => ({
    meta: [
      { title: "Participante — Beta" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ParticipantPage,
});

function ParticipantPage() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();
  const funnelFn = useServerFn(getParticipantFunnel);
  const notesFn = useServerFn(listBetaNotes);
  const addNoteFn = useServerFn(addBetaNote);
  const delNoteFn = useServerFn(deleteBetaNote);
  const upsertFn = useServerFn(upsertBetaParticipant);

  const funnel = useQuery({
    queryKey: ["beta-funnel", userId],
    queryFn: () => funnelFn({ data: { user_id: userId } }),
  });
  const notes = useQuery({
    queryKey: ["beta-notes", userId],
    queryFn: () => notesFn({ data: { user_id: userId } }),
  });

  const [note, setNote] = useState("");
  const addM = useMutation({
    mutationFn: (body: string) => addNoteFn({ data: { user_id: userId, body } }),
    onSuccess: () => {
      setNote("");
      toast.success("Nota adicionada.");
      qc.invalidateQueries({ queryKey: ["beta-notes", userId] });
      qc.invalidateQueries({ queryKey: ["beta-funnel", userId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao salvar nota."),
  });
  const delM = useMutation({
    mutationFn: (id: string) => delNoteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Nota removida.");
      qc.invalidateQueries({ queryKey: ["beta-notes", userId] });
    },
  });
  const updateM = useMutation({
    mutationFn: (patch: { cohort?: string; source?: string; status?: string }) =>
      upsertFn({ data: { user_id: userId, ...patch } }),
    onSuccess: () => {
      toast.success("Participante atualizado.");
      qc.invalidateQueries({ queryKey: ["beta-funnel", userId] });
    },
  });

  const p: any = funnel.data?.participant;
  const profile: any = funnel.data?.profile;

  return (
    <div className="space-y-6">
      <Link
        to="/admin/beta"
        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--gray-ink)] hover:text-[var(--navy)]"
      >
        <ArrowLeft size={14} /> Beta
      </Link>

      <PageHeader
        title={profile?.full_name ?? profile?.email ?? userId.slice(0, 8)}
        description={profile?.email ?? "Participante do beta controlado."}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <section className="rounded-xl border border-black/[0.06] bg-white p-5">
            <h2 className="text-sm font-semibold text-[var(--navy)]">Funil</h2>
            <ol className="mt-4 space-y-2">
              {(funnel.data?.steps ?? []).map((s: any) => (
                <li
                  key={s.key}
                  className="flex items-center justify-between rounded-lg border border-black/5 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <StateIcon state={s.state} />
                    <span className="text-sm">{s.label}</span>
                  </div>
                  <span className="text-[11px] text-[var(--gray-ink)]">
                    {s.at ? new Date(s.at).toLocaleString("pt-BR") : "—"}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-xl border border-black/[0.06] bg-white p-5">
            <h2 className="text-sm font-semibold text-[var(--navy)]">Eventos recentes</h2>
            <ul className="mt-3 divide-y divide-black/5 text-xs">
              {(funnel.data?.recentEvents ?? []).map((e: any, i: number) => (
                <li key={i} className="flex items-center justify-between py-1.5">
                  <span className="font-medium">{e.event_name}</span>
                  <span className="text-[var(--gray-ink)]">
                    {new Date(e.created_at).toLocaleString("pt-BR")}
                  </span>
                </li>
              ))}
              {(funnel.data?.recentEvents ?? []).length === 0 && (
                <li className="py-3 text-[var(--gray-ink)]">Sem eventos registrados.</li>
              )}
            </ul>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-xl border border-black/[0.06] bg-white p-5">
            <h2 className="text-sm font-semibold text-[var(--navy)]">Configuração</h2>
            <div className="mt-3 space-y-3 text-xs">
              <label className="block">
                <span className="text-[var(--gray-ink)]">Coorte</span>
                <input
                  defaultValue={p?.cohort ?? "beta_01"}
                  onBlur={(e) =>
                    e.target.value !== p?.cohort && updateM.mutate({ cohort: e.target.value })
                  }
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-[var(--gray-ink)]">Origem</span>
                <input
                  defaultValue={p?.source ?? ""}
                  onBlur={(e) =>
                    e.target.value !== (p?.source ?? "") &&
                    updateM.mutate({ source: e.target.value })
                  }
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-[var(--gray-ink)]">Situação</span>
                <select
                  defaultValue={p?.status ?? "convidado"}
                  onChange={(e) => updateM.mutate({ status: e.target.value })}
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
                >
                  {[
                    "convidado",
                    "cadastrado",
                    "assinatura_pendente",
                    "ativado",
                    "em_andamento",
                    "inativo",
                    "concluido",
                    "requer_atencao",
                  ].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="rounded-xl border border-black/[0.06] bg-white p-5">
            <h2 className="text-sm font-semibold text-[var(--navy)]">Notas internas</h2>
            <div className="mt-3 space-y-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Registre uma observação operacional…"
                rows={3}
                className="w-full rounded-md border border-black/10 px-2 py-1.5 text-xs"
              />
              <button
                disabled={!note.trim() || addM.isPending}
                onClick={() => addM.mutate(note.trim())}
                className="w-full rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                Adicionar nota
              </button>
            </div>
            <ul className="mt-4 space-y-2">
              {(notes.data?.items ?? []).map((n: any) => (
                <li key={n.id} className="rounded-md border border-black/5 bg-[var(--gray-soft)]/40 p-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="whitespace-pre-wrap text-xs">{n.body}</p>
                    <button
                      onClick={() => delM.mutate(n.id)}
                      className="text-[var(--gray-ink)] hover:text-red-600"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <div className="mt-1 text-[10px] text-[var(--gray-ink)]">
                    {n.author?.email ?? "admin"} • {new Date(n.created_at).toLocaleString("pt-BR")}
                  </div>
                </li>
              ))}
              {(notes.data?.items ?? []).length === 0 && (
                <li className="text-xs text-[var(--gray-ink)]">Sem notas.</li>
              )}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function StateIcon({ state }: { state: string }) {
  if (state === "done") return <Check size={14} className="text-emerald-600" />;
  if (state === "error") return <AlertCircle size={14} className="text-red-600" />;
  if (state === "na") return <Minus size={14} className="text-[var(--gray-ink)]" />;
  return <Clock size={14} className="text-amber-600" />;
}
